import { Switch } from "@/components/ui/switch";
import React, { useEffect, useState } from "react";
import { AlertOctagon, AlertTriangle, BookOpen, CheckCircle2, ChevronDown, Download, Info, Sparkles } from "lucide-react";
import { MONTHS_FULL, pointSub } from "./eaFormat";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const cache = {};
function useGuide(key) {
  const [g, setG] = useState(cache[key] || null);
  useEffect(() => {
    if (cache[key]) return;
    api.get(`/ea/guide/${key}`).then(({ data }) => { cache[key] = data; setG(data); }).catch(() => setG(null));
  }, [key]);
  return g;
}

async function downloadPdf(key, title) {
  try {
    const res = await api.get(`/ea/guide/${key}.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${title.normalize("NFD").replace(/[^A-Za-z0-9]+/g, "_").replace(/_+$/, "")}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch { toast.error("Download non riuscito"); }
}

// Profili/scenari pronti: compilano i parametri, la conferma resta all'utente.
export function EaPresetButtons({ guideKey, onApply, label = "Profili pronti:", testid = "ea-presets" }) {
  const g = useGuide(guideKey);
  if (!g) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <span className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1"><Sparkles size={13} /> {label}</span>
      {g.presets.map((p) => (
        <button key={p.key} title={`${p.description} · ${p.summary}`} data-testid={`${testid}-${p.key}`} onClick={() => onApply(p)}
          className="px-3 py-1 rounded-full text-xs font-semibold border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100">{p.label}</button>
      ))}
    </div>
  );
}

export function EaGuideButton({ onClick, testid = "ea-guide-open" }) {
  return (
    <button onClick={onClick} className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid={testid}>
      <BookOpen size={15} /> Come si usa
    </button>
  );
}

export function EaGuidePanel({ guideKey, open, setOpen, section, onApply }) {
  const g = useGuide(guideKey);
  useEffect(() => {
    if (!open || !section) return;
    const t = setTimeout(() => document.getElementById(`ea-guide-sec-${section}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    return () => clearTimeout(t);
  }, [open, section]);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="ea-guide-panel">
        <SheetHeader><SheetTitle className="font-display text-xl">{g?.title || "Vademecum"}</SheetTitle></SheetHeader>
        {g && (
          <button onClick={() => downloadPdf(guideKey, g.title)} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:underline" data-testid="ea-guide-pdf">
            <Download size={15} /> Scarica in PDF
          </button>
        )}
        {!g ? <div className="text-sm text-slate-400 mt-6">Caricamento…</div> : (
          <div className="mt-5 space-y-6 text-sm text-slate-700 leading-relaxed">
            {g.sections.map((s, i) => (
              <section key={s.title} id={`ea-guide-sec-${i + 1}`} data-testid="ea-guide-section"
                className={section === i + 1 ? "rounded-lg bg-blue-50/70 -mx-2 px-2 py-2" : ""}>
                <h3 className="font-display font-semibold text-slate-900 mb-2">{s.title}</h3>
                {(s.paragraphs || []).map((p) => <p key={p} className="mb-2">{p}</p>)}
                {s.items && <ul className="list-disc pl-5 space-y-1.5">{s.items.map((it) => <li key={it} dangerouslySetInnerHTML={{ __html: it }} />)}</ul>}
                {s.title.startsWith(g.preset_section) && onApply && (
                  <div className="mt-3 space-y-2">
                    {g.presets.map((p) => (
                      <div key={p.key} className="rounded-lg border border-slate-200 p-3" data-testid={`ea-guide-preset-${p.key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-slate-900">{p.label}</div>
                          <button onClick={() => onApply(p)} className="text-xs font-semibold text-blue-700 hover:underline" data-testid={`ea-guide-apply-${p.key}`}>Applica</button>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{p.description}</div>
                        <div className="text-xs text-slate-600 mt-1 font-mono-nums">{p.summary}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const LEVEL = {
  alert: [AlertOctagon, "text-red-600", "border-red-200 bg-red-50/70"],
  warning: [AlertTriangle, "text-amber-600", "border-amber-200 bg-amber-50/70"],
  info: [Info, "text-blue-600", "border-blue-100 bg-blue-50/50"],
  ok: [CheckCircle2, "text-green-600", "border-green-200 bg-green-50/60"],
};

function InsightCard({ m, onOpenSection, testid }) {
  const [Icon, ic, box] = LEVEL[m.level];
  return (
    <div className={`rounded-lg border p-3 flex gap-3 ${box}`} data-testid={testid} data-pdf="note" data-pdf-level={m.level}>
      <Icon size={18} className={`${ic} shrink-0 mt-0.5`} />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900" data-pdf-ntitle>{m.title}</div>
        <div className="text-xs text-slate-600 mt-0.5" data-pdf-ntext>{m.text}</div>
        <button onClick={() => onOpenSection(m.section)} className="text-xs font-semibold text-blue-700 hover:underline mt-1" data-testid={`${testid}-link`}>
          Approfondisci nel vademecum §{m.section}
        </button>
      </div>
    </div>
  );
}

const BADGE = { alert: "bg-red-100 text-red-700", warning: "bg-amber-100 text-amber-700", info: "bg-blue-100 text-blue-700", ok: "bg-green-100 text-green-700" };
const BADGE_LABEL = { alert: "critiche", warning: "attenzioni", info: "info", ok: "ok" };

function InsightMonth({ m, year, latest, onOpenSection, testid, pod }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200" data-testid={testid} data-pdf-ctx={`${pod} · ${MONTHS_FULL[m.month - 1]} ${year}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50" data-testid={`${testid}-toggle`}>
        <span className="text-sm font-semibold text-slate-900">{MONTHS_FULL[m.month - 1]} {year}{latest && <span className="ml-2 text-xs font-medium text-blue-700">ultimo mese con dati</span>}</span>
        <span className="flex items-center gap-1.5">
          {Object.keys(BADGE).filter((k) => m.levels?.[k]).map((k) => <span key={k} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${BADGE[k]}`}>{m.levels[k]} {BADGE_LABEL[k]}</span>)}
          <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="p-3 pt-0">
          {!m.messages.length ? <div className="text-sm text-slate-500">Nessuna indicazione per il mese.</div> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {m.messages.map((x, i) => <InsightCard key={`${x.title}-${i}`} m={x} onOpenSection={onOpenSection} testid={`${testid}-msg-${i}`} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Interpretazione per Punto di fornitura e mese (dal più recente).
export function EaInsightsGrouped({ data, onOpenSection, loading, latestOnly = false }) {
  const groups = data?.groups || [];
  const [all, setAll] = useState(!latestOnly);
  const many = groups.some((g) => g.months.length > 1);
  return (
    <div className="card-elevated p-5" data-testid="ea-insights">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="font-display font-semibold text-slate-900">Interpretazione dei risultati</div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs text-slate-400">{all ? "Per punto di fornitura e mese" : "Ultimo mese disponibile per punto di fornitura"} · regole del vademecum · si aggiorna con filtri e soglie</div>
          {latestOnly && many && <label className="inline-flex items-center gap-2 text-xs text-slate-600"><Switch checked={all} onCheckedChange={setAll} data-testid="ea-insights-show-all" /> Mostra tutti i mesi</label>}
        </div>
      </div>
      {loading ? <div className="text-sm text-slate-400">Analisi in corso…</div> : !groups.length ? <div className="text-sm text-slate-500">Nessuna indicazione per il periodo selezionato.</div> : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.code} data-testid={`ea-insights-pod-${g.identifier}`}>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">POD {g.identifier}{g.label || g.address ? ` · ${pointSub(g)}` : ""}</div>
              <div className="space-y-2">
                {(all ? g.months : g.months.slice(0, 1)).map((m, i) => <InsightMonth key={m.month} m={m} year={data.year} pod={`POD ${g.identifier}`} latest={i === 0} onOpenSection={onOpenSection} testid={`ea-insights-${g.identifier}-${m.month}`} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
